import React, { useMemo, useCallback, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Line, Text } from '@react-three/drei';

// ============================================
// SOLID BASIS-KOMPONENTE
// ============================================
// Alle 3D-Körper erben von diesem Grundkonzept:
// - Origin (Mittelpunkt)
// - Lokale Achsen (X, Y, Z)
// - Rotation
// - Bounding Box
// ============================================

// Hilfsfunktion: Euler-Rotation zu Quaternion
function eulerToQuaternion(rotationDegrees) {
  const euler = new THREE.Euler(
    THREE.MathUtils.degToRad(rotationDegrees.x || 0),
    THREE.MathUtils.degToRad(rotationDegrees.y || 0),
    THREE.MathUtils.degToRad(rotationDegrees.z || 0),
    'XYZ'
  );
  return new THREE.Quaternion().setFromEuler(euler);
}

// Hilfsfunktion: Lokale Achsen nach Rotation berechnen
function getRotatedAxes(rotationDegrees) {
  const quat = eulerToQuaternion(rotationDegrees);

  const xAxis = new THREE.Vector3(1, 0, 0).applyQuaternion(quat);
  const yAxis = new THREE.Vector3(0, 1, 0).applyQuaternion(quat);
  const zAxis = new THREE.Vector3(0, 0, 1).applyQuaternion(quat);

  return { x: xAxis, y: yAxis, z: zAxis };
}

// ============================================
// ACHSEN-DARSTELLUNG (Gizmo)
// ============================================
export function AxisGizmo({ origin, rotation = { x: 0, y: 0, z: 0 }, size = 500, visible = true }) {
  const axes = useMemo(() => getRotatedAxes(rotation), [rotation.x, rotation.y, rotation.z]);

  if (!visible) return null;

  const o = new THREE.Vector3(origin.x, origin.y, origin.z);

  return (
    <group>
      {/* X-Achse (Rot) */}
      <Line
        points={[o, o.clone().add(axes.x.clone().multiplyScalar(size))]}
        color="#ef4444"
        lineWidth={3}
      />
      {/* Y-Achse (Grün) */}
      <Line
        points={[o, o.clone().add(axes.y.clone().multiplyScalar(size))]}
        color="#22c55e"
        lineWidth={3}
      />
      {/* Z-Achse (Blau) */}
      <Line
        points={[o, o.clone().add(axes.z.clone().multiplyScalar(size))]}
        color="#3b82f6"
        lineWidth={3}
      />
      {/* Origin-Punkt */}
      <mesh position={[origin.x, origin.y, origin.z]}>
        <sphereGeometry args={[50, 16, 16]} />
        <meshBasicMaterial color="#ffffff" />
      </mesh>
    </group>
  );
}

// ============================================
// BOX (Quader)
// ============================================
export function Box3D({
  solid,
  isSelected = false,
  isHovered = false,
  onSelect,
  onHover,
  viewMode = '2d',
  showAxes = false,
  showDimensions = true,
}) {
  const { camera, gl } = useThree();

  const {
    id,
    origin = { x: 0, y: 0, z: 0 },
    rotation = { x: 0, y: 0, z: 0 },
    dimensions = { width: 1000, depth: 1000, height: 1000 },
    color = '#6b7280',
    opacity = 0.7,
    name,
  } = solid;

  const { width, depth, height } = dimensions;

  // Euler-Rotation für Three.js
  const eulerRotation = useMemo(() => [
    THREE.MathUtils.degToRad(rotation.x || 0),
    THREE.MathUtils.degToRad(rotation.y || 0),
    THREE.MathUtils.degToRad(rotation.z || 0),
  ], [rotation.x, rotation.y, rotation.z]);

  // Farbe basierend auf Zustand
  const displayColor = useMemo(() => {
    if (isSelected) return '#3b82f6';
    if (isHovered) return '#60a5fa';
    return color;
  }, [isSelected, isHovered, color]);

  // Event Handler
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onSelect?.(id, e.nativeEvent || e);
  }, [id, onSelect]);

  const handlePointerOver = useCallback((e) => {
    e.stopPropagation();
    onHover?.('solid', id);
    document.body.style.cursor = 'pointer';
  }, [id, onHover]);

  const handlePointerOut = useCallback(() => {
    onHover?.(null, null);
    document.body.style.cursor = 'default';
  }, [onHover]);

  // 8 Eckpunkte des Quaders (lokal, vor Rotation)
  const vertices = useMemo(() => {
    const hw = width / 2;
    const hd = depth / 2;
    const hh = height / 2;

    return [
      // Unten (Z = -hh)
      new THREE.Vector3(-hw, -hd, -hh), // 0: hinten-links-unten
      new THREE.Vector3(hw, -hd, -hh),  // 1: hinten-rechts-unten
      new THREE.Vector3(hw, hd, -hh),   // 2: vorne-rechts-unten
      new THREE.Vector3(-hw, hd, -hh),  // 3: vorne-links-unten
      // Oben (Z = +hh)
      new THREE.Vector3(-hw, -hd, hh),  // 4: hinten-links-oben
      new THREE.Vector3(hw, -hd, hh),   // 5: hinten-rechts-oben
      new THREE.Vector3(hw, hd, hh),    // 6: vorne-rechts-oben
      new THREE.Vector3(-hw, hd, hh),   // 7: vorne-links-oben
    ];
  }, [width, depth, height]);

  // Kanten des Quaders (12 Kanten)
  const edges = useMemo(() => [
    // Untere Fläche
    [0, 1], [1, 2], [2, 3], [3, 0],
    // Obere Fläche
    [4, 5], [5, 6], [6, 7], [7, 4],
    // Vertikale Kanten
    [0, 4], [1, 5], [2, 6], [3, 7],
  ], []);

  // 2D Footprint (MUSS vor conditional return stehen!)
  const footprint = useMemo(() => {
    const quat = eulerToQuaternion(rotation);
    return [0, 1, 2, 3, 0].map(i => {
      const v = vertices[i].clone();
      v.applyQuaternion(quat);
      return new THREE.Vector3(
        origin.x + v.x,
        origin.y + v.y,
        1 // Z für 2D-Darstellung
      );
    });
  }, [vertices, rotation, origin]);

  // 2D-Ansicht: Nur Grundfläche als Rechteck
  if (viewMode === '2d') {
    return (
      <group>
        {/* Gefüllte Fläche */}
        <mesh
          position={[origin.x, origin.y, 0.5]}
          rotation={[0, 0, THREE.MathUtils.degToRad(rotation.z || 0)]}
          onClick={handleClick}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
        >
          <planeGeometry args={[width, depth]} />
          <meshBasicMaterial
            color={displayColor}
            transparent
            opacity={isSelected ? 0.6 : 0.4}
            side={THREE.DoubleSide}
          />
        </mesh>

        {/* Umriss */}
        <Line
          points={footprint}
          color={isSelected ? '#3b82f6' : '#374151'}
          lineWidth={isSelected ? 3 : 2}
        />

        {/* Achsen (wenn ausgewählt) */}
        {isSelected && (
          <AxisGizmo
            origin={{ x: origin.x, y: origin.y, z: 2 }}
            rotation={rotation}
            size={Math.max(width, depth) * 0.6}
            visible={showAxes || isSelected}
          />
        )}

        {/* Name/Label */}
        {name && (
          <Text
            position={[origin.x, origin.y, 3]}
            fontSize={Math.min(width, depth) * 0.15}
            color="#374151"
            anchorX="center"
            anchorY="middle"
          >
            {name}
          </Text>
        )}
      </group>
    );
  }

  // 3D-Ansicht
  return (
    <group>
      {/* Box-Mesh */}
      <mesh
        position={[origin.x, origin.y, origin.z]}
        rotation={eulerRotation}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <boxGeometry args={[width, depth, height]} />
        <meshStandardMaterial
          color={displayColor}
          transparent
          opacity={isSelected ? 0.8 : opacity}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Kanten (Wireframe) */}
      <group position={[origin.x, origin.y, origin.z]} rotation={eulerRotation}>
        {edges.map(([i, j], idx) => (
          <Line
            key={idx}
            points={[vertices[i], vertices[j]]}
            color={isSelected ? '#1e40af' : '#374151'}
            lineWidth={isSelected ? 2 : 1}
          />
        ))}
      </group>

      {/* Achsen-Gizmo */}
      {(showAxes || isSelected) && (
        <AxisGizmo
          origin={origin}
          rotation={rotation}
          size={Math.max(width, depth, height) * 0.6}
        />
      )}

      {/* Dimensionen */}
      {showDimensions && isSelected && (
        <>
          {/* Breite (X) */}
          <Text
            position={[origin.x, origin.y - depth / 2 - 200, origin.z - height / 2]}
            fontSize={150}
            color="#ef4444"
            anchorX="center"
            anchorY="middle"
          >
            {(width / 1000).toFixed(2)} m
          </Text>
          {/* Tiefe (Y) */}
          <Text
            position={[origin.x + width / 2 + 200, origin.y, origin.z - height / 2]}
            fontSize={150}
            color="#22c55e"
            anchorX="center"
            anchorY="middle"
            rotation={[0, 0, Math.PI / 2]}
          >
            {(depth / 1000).toFixed(2)} m
          </Text>
          {/* Höhe (Z) */}
          <Text
            position={[origin.x + width / 2 + 200, origin.y - depth / 2 - 200, origin.z]}
            fontSize={150}
            color="#3b82f6"
            anchorX="center"
            anchorY="middle"
          >
            {(height / 1000).toFixed(2)} m
          </Text>
        </>
      )}

      {/* Name */}
      {name && (
        <Text
          position={[origin.x, origin.y, origin.z + height / 2 + 200]}
          fontSize={200}
          color="#374151"
          anchorX="center"
          anchorY="bottom"
        >
          {name}
        </Text>
      )}
    </group>
  );
}

// ============================================
// CYLINDER (Zylinder)
// ============================================
export function Cylinder3D({
  solid,
  isSelected = false,
  isHovered = false,
  onSelect,
  onHover,
  viewMode = '2d',
  showAxes = false,
  showDimensions = true,
}) {
  const {
    id,
    origin = { x: 0, y: 0, z: 0 },
    rotation = { x: 0, y: 0, z: 0 },
    dimensions = { radius: 500, height: 1000, segments: 32 },
    color = '#6b7280',
    opacity = 0.7,
    name,
  } = solid;

  const { radius, height, segments = 32 } = dimensions;

  const eulerRotation = useMemo(() => [
    THREE.MathUtils.degToRad(rotation.x || 0),
    THREE.MathUtils.degToRad(rotation.y || 0),
    THREE.MathUtils.degToRad(rotation.z || 0),
  ], [rotation.x, rotation.y, rotation.z]);

  const displayColor = useMemo(() => {
    if (isSelected) return '#3b82f6';
    if (isHovered) return '#60a5fa';
    return color;
  }, [isSelected, isHovered, color]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onSelect?.(id, e.nativeEvent || e);
  }, [id, onSelect]);

  const handlePointerOver = useCallback((e) => {
    e.stopPropagation();
    onHover?.('solid', id);
    document.body.style.cursor = 'pointer';
  }, [id, onHover]);

  const handlePointerOut = useCallback(() => {
    onHover?.(null, null);
    document.body.style.cursor = 'default';
  }, [onHover]);

  // 2D: Kreis (Draufsicht)
  if (viewMode === '2d') {
    return (
      <group>
        <mesh
          position={[origin.x, origin.y, 0.5]}
          rotation={[0, 0, THREE.MathUtils.degToRad(rotation.z || 0)]}
          onClick={handleClick}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
        >
          <circleGeometry args={[radius, segments]} />
          <meshBasicMaterial
            color={displayColor}
            transparent
            opacity={isSelected ? 0.6 : 0.4}
            side={THREE.DoubleSide}
          />
        </mesh>

        {/* Umriss */}
        <Line
          points={Array.from({ length: segments + 1 }, (_, i) => {
            const angle = (i / segments) * Math.PI * 2;
            return new THREE.Vector3(
              origin.x + radius * Math.cos(angle),
              origin.y + radius * Math.sin(angle),
              1
            );
          })}
          color={isSelected ? '#3b82f6' : '#374151'}
          lineWidth={isSelected ? 3 : 2}
        />

        {isSelected && (
          <AxisGizmo
            origin={{ x: origin.x, y: origin.y, z: 2 }}
            rotation={rotation}
            size={radius * 1.2}
            visible={showAxes || isSelected}
          />
        )}

        {name && (
          <Text
            position={[origin.x, origin.y, 3]}
            fontSize={radius * 0.3}
            color="#374151"
            anchorX="center"
            anchorY="middle"
          >
            {name}
          </Text>
        )}
      </group>
    );
  }

  // 3D
  return (
    <group>
      <mesh
        position={[origin.x, origin.y, origin.z]}
        rotation={eulerRotation}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <cylinderGeometry args={[radius, radius, height, segments]} />
        <meshStandardMaterial
          color={displayColor}
          transparent
          opacity={isSelected ? 0.8 : opacity}
          side={THREE.DoubleSide}
        />
      </mesh>

      {(showAxes || isSelected) && (
        <AxisGizmo
          origin={origin}
          rotation={rotation}
          size={Math.max(radius * 2, height) * 0.6}
        />
      )}

      {showDimensions && isSelected && (
        <>
          <Text
            position={[origin.x + radius + 200, origin.y, origin.z]}
            fontSize={150}
            color="#ef4444"
            anchorX="left"
            anchorY="middle"
          >
            r={( radius / 1000).toFixed(2)}m
          </Text>
          <Text
            position={[origin.x, origin.y, origin.z + height / 2 + 200]}
            fontSize={150}
            color="#3b82f6"
            anchorX="center"
            anchorY="bottom"
          >
            h={( height / 1000).toFixed(2)}m
          </Text>
        </>
      )}

      {name && (
        <Text
          position={[origin.x, origin.y, origin.z + height / 2 + 400]}
          fontSize={200}
          color="#374151"
          anchorX="center"
          anchorY="bottom"
        >
          {name}
        </Text>
      )}
    </group>
  );
}

// ============================================
// SPHERE (Kugel)
// ============================================
export function Sphere3D({
  solid,
  isSelected = false,
  isHovered = false,
  onSelect,
  onHover,
  viewMode = '2d',
  showAxes = false,
  showDimensions = true,
}) {
  const {
    id,
    origin = { x: 0, y: 0, z: 0 },
    rotation = { x: 0, y: 0, z: 0 },
    dimensions = { radius: 500 },
    color = '#6b7280',
    opacity = 0.7,
    name,
  } = solid;

  const { radius, widthSegments = 32, heightSegments = 16 } = dimensions;

  const displayColor = useMemo(() => {
    if (isSelected) return '#3b82f6';
    if (isHovered) return '#60a5fa';
    return color;
  }, [isSelected, isHovered, color]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onSelect?.(id, e.nativeEvent || e);
  }, [id, onSelect]);

  const handlePointerOver = useCallback((e) => {
    e.stopPropagation();
    onHover?.('solid', id);
    document.body.style.cursor = 'pointer';
  }, [id, onHover]);

  const handlePointerOut = useCallback(() => {
    onHover?.(null, null);
    document.body.style.cursor = 'default';
  }, [onHover]);

  // 2D: Kreis
  if (viewMode === '2d') {
    return (
      <group>
        <mesh
          position={[origin.x, origin.y, 0.5]}
          onClick={handleClick}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
        >
          <circleGeometry args={[radius, 32]} />
          <meshBasicMaterial
            color={displayColor}
            transparent
            opacity={isSelected ? 0.6 : 0.4}
            side={THREE.DoubleSide}
          />
        </mesh>

        <Line
          points={Array.from({ length: 33 }, (_, i) => {
            const angle = (i / 32) * Math.PI * 2;
            return new THREE.Vector3(
              origin.x + radius * Math.cos(angle),
              origin.y + radius * Math.sin(angle),
              1
            );
          })}
          color={isSelected ? '#3b82f6' : '#374151'}
          lineWidth={isSelected ? 3 : 2}
        />

        {name && (
          <Text
            position={[origin.x, origin.y, 3]}
            fontSize={radius * 0.3}
            color="#374151"
            anchorX="center"
            anchorY="middle"
          >
            {name}
          </Text>
        )}
      </group>
    );
  }

  // 3D
  return (
    <group>
      <mesh
        position={[origin.x, origin.y, origin.z]}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <sphereGeometry args={[radius, widthSegments, heightSegments]} />
        <meshStandardMaterial
          color={displayColor}
          transparent
          opacity={isSelected ? 0.8 : opacity}
          side={THREE.DoubleSide}
        />
      </mesh>

      {(showAxes || isSelected) && (
        <AxisGizmo
          origin={origin}
          rotation={rotation}
          size={radius * 1.5}
        />
      )}

      {showDimensions && isSelected && (
        <Text
          position={[origin.x + radius + 200, origin.y, origin.z]}
          fontSize={150}
          color="#ef4444"
          anchorX="left"
          anchorY="middle"
        >
          r={(radius / 1000).toFixed(2)}m
        </Text>
      )}

      {name && (
        <Text
          position={[origin.x, origin.y, origin.z + radius + 200]}
          fontSize={200}
          color="#374151"
          anchorX="center"
          anchorY="bottom"
        >
          {name}
        </Text>
      )}
    </group>
  );
}

// ============================================
// EXTRUSION (Extrudierter Körper)
// ============================================
export function Extrusion3D({
  solid,
  isSelected = false,
  isHovered = false,
  onSelect,
  onHover,
  viewMode = '2d',
  showAxes = false,
}) {
  const {
    id,
    origin = { x: 0, y: 0, z: 0 },
    profilePoints = [],
    direction = { x: 0, y: 0, z: 1000 },
    color = '#6b7280',
    opacity = 0.7,
    name,
  } = solid;

  const displayColor = useMemo(() => {
    if (isSelected) return '#3b82f6';
    if (isHovered) return '#60a5fa';
    return color;
  }, [isSelected, isHovered, color]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onSelect?.(id, e.nativeEvent || e);
  }, [id, onSelect]);

  const handlePointerOver = useCallback((e) => {
    e.stopPropagation();
    onHover?.('solid', id);
    document.body.style.cursor = 'pointer';
  }, [id, onHover]);

  const handlePointerOut = useCallback(() => {
    onHover?.(null, null);
    document.body.style.cursor = 'default';
  }, [onHover]);

  // Create shape from profile points
  const shape = useMemo(() => {
    if (profilePoints.length < 3) return null;
    const s = new THREE.Shape();
    s.moveTo(profilePoints[0].x, profilePoints[0].y);
    for (let i = 1; i < profilePoints.length; i++) {
      s.lineTo(profilePoints[i].x, profilePoints[i].y);
    }
    s.closePath();
    return s;
  }, [profilePoints]);

  // Extrusion settings
  const extrudeSettings = useMemo(() => ({
    depth: Math.sqrt(direction.x * direction.x + direction.y * direction.y + direction.z * direction.z),
    bevelEnabled: false,
  }), [direction]);

  if (!shape || profilePoints.length < 3) return null;

  // 2D: Profil anzeigen
  if (viewMode === '2d') {
    const pts = [...profilePoints, profilePoints[0]].map(p =>
      new THREE.Vector3(p.x, p.y, 1)
    );

    return (
      <group>
        <Line
          points={pts}
          color={isSelected ? '#3b82f6' : '#374151'}
          lineWidth={isSelected ? 3 : 2}
        />

        {name && (
          <Text
            position={[origin.x, origin.y, 3]}
            fontSize={200}
            color="#374151"
            anchorX="center"
            anchorY="middle"
          >
            {name}
          </Text>
        )}
      </group>
    );
  }

  // 3D
  return (
    <group>
      <mesh
        position={[0, 0, 0]}
        rotation={[Math.PI / 2, 0, 0]} // Extrude along Z
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <extrudeGeometry args={[shape, extrudeSettings]} />
        <meshStandardMaterial
          color={displayColor}
          transparent
          opacity={isSelected ? 0.8 : opacity}
          side={THREE.DoubleSide}
        />
      </mesh>

      {(showAxes || isSelected) && (
        <AxisGizmo
          origin={origin}
          rotation={{ x: 0, y: 0, z: 0 }}
          size={500}
        />
      )}

      {name && (
        <Text
          position={[origin.x, origin.y, direction.z + 200]}
          fontSize={200}
          color="#374151"
          anchorX="center"
          anchorY="bottom"
        >
          {name}
        </Text>
      )}
    </group>
  );
}

// ============================================
// SOLID FACTORY
// ============================================
// Erstellt einen neuen Solid basierend auf Typ
export function createSolid(type, params = {}) {
  const baseId = `solid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const base = {
    id: params.id || baseId,
    type,
    origin: params.origin || { x: 0, y: 0, z: 0 },
    rotation: params.rotation || { x: 0, y: 0, z: 0 },
    color: params.color || '#6b7280',
    opacity: params.opacity ?? 0.7,
    name: params.name || null,
  };

  switch (type) {
    case 'box':
      return {
        ...base,
        dimensions: {
          width: params.width || 1000,
          depth: params.depth || 1000,
          height: params.height || 1000,
        },
      };

    case 'cylinder':
      return {
        ...base,
        dimensions: {
          radius: params.radius || 500,
          height: params.height || 1000,
          segments: params.segments || 32,
        },
      };

    case 'sphere':
      return {
        ...base,
        dimensions: {
          radius: params.radius || 500,
          widthSegments: params.widthSegments || 32,
          heightSegments: params.heightSegments || 16,
        },
      };

    case 'extrusion':
      return {
        ...base,
        profilePoints: params.profilePoints || [],
        direction: params.direction || { x: 0, y: 0, z: 1000 },
      };

    default:
      return base;
  }
}

// ============================================
// SOLID RENDERER
// ============================================
// Rendert einen Solid basierend auf seinem Typ
export function Solid3D({ solid, ...props }) {
  switch (solid.type) {
    case 'box':
    case 'Box':
      return <Box3D solid={solid} {...props} />;

    case 'cylinder':
    case 'Cylinder':
      return <Cylinder3D solid={solid} {...props} />;

    case 'sphere':
    case 'Sphere':
      return <Sphere3D solid={solid} {...props} />;

    case 'extrusion':
    case 'Extrusion':
      return <Extrusion3D solid={solid} {...props} />;

    default:
      console.warn(`Unknown solid type: ${solid.type}`);
      return null;
  }
}

export default Solid3D;
